import {
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { AccordionGeneralityFormVM } from 'front-office/view-models/certificate/generality-form';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { PmiButton } from 'shell/components/shared/button';
import ChooseDate from 'shell/components/shared/choice-date';
import { DateTime } from 'luxon';
import ItalianForeignState from './italian-foreign-state';
import Document from './document';
import { noop } from 'shell/utils/noop';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponseAnprData } from 'shell/models/person';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { AnprVM } from 'shell/view-models/search-anpr';
import { HttpResponse } from 'shell/models/http-response';
import { transformerPersonGeneralityList } from './transformer-anpr';
import { CertificateFormVM } from 'front-office/view-models/certificate/certificate-form';
import { transformSearchPersonDataset } from 'shell/utils/transform-search-person';
import { AccordionApplicantFormVM } from 'front-office/view-models/certificate/applicant-form';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import Table from 'shell/components/shared/table';
import { HeadCell } from 'shell/components/shared/table/types';
import { PersonVM } from 'shell/view-models/search-person';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { FormikErrors, FormikTouched } from 'formik';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import PmiTextField from 'shell/components/shared/text-field';
import InputKeyboard from 'shell/components/shared/input-keyboard';
import ListGender from 'shell/components/shared/gender';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import ErrorModal from 'shell/components/shared/anomalie-modal';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';

export const headCells: HeadCell<PersonVM, keyof PersonVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato',
  },
  {
    id: 'anpr',
    label: 'ANPR',
  },
  {
    id: 'action',
    label: 'Azione',
  },
];

interface AccordionGeneralityFormProps {
  disabled?: boolean;
  isAuthentic?: boolean;
  values?: AccordionGeneralityFormVM;
  applicant?: AccordionApplicantFormVM;
  errors?: FormikErrors<AccordionGeneralityFormVM>;
  touched?: FormikTouched<AccordionGeneralityFormVM>;
  hasAccountholder?: boolean;
  isDeclaration?: boolean;
  onChange: (
    key: keyof AccordionGeneralityFormVM,
    value: AccordionGeneralityFormVM[keyof AccordionGeneralityFormVM]
  ) => void;
  onChangeValues: (values: AccordionGeneralityFormVM) => void;
  setValues: (values: CertificateFormVM) => void;
  allValues: CertificateFormVM;
  visibilityTable: boolean;
  setVisibilityTable: (visibility: boolean) => void;
  valuesPerson?: AccordionApplicantFormVM;
  errorsPerson?: FormikErrors<AccordionApplicantFormVM>;
  touchedPerson?: FormikTouched<AccordionApplicantFormVM>;
  onChangePerson: (
    key: keyof AccordionApplicantFormVM,
    value: AccordionApplicantFormVM[keyof AccordionApplicantFormVM]
  ) => void;
  url?: string;
}

const AccordionGeneralityFormComponent = ({
  disabled,
  values,
  url = `${__PERSON_NEW_API_URL__}/persona/ricerca-certificati`,
  applicant,
  onChange,
  onChangeValues,
  setValues,
  allValues,
  errors,
  touched,
  visibilityTable,
  setVisibilityTable,
  valuesPerson,
  errorsPerson,
  touchedPerson,
  onChangePerson,
  isDeclaration,
  hasAccountholder = true,
  isAuthentic,
}: AccordionGeneralityFormProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const [openErrorModal, setOpenErrorModal] = useState(false);

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  useEffect(() => {
    if (!allValues.visibilityErrorApplicant) {
      setVisibility({ error: false, warning: false });
    }
  }, [allValues.visibilityErrorApplicant]);

  // Handle choice date
  const handleChooseDateChange = useEventCallback(
    (date: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeValues({ birthDate: date, birthChoiceDate: choice });
    }
  );

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>(
    url,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonGeneralityList,
    }
  );

  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>(url, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerPersonGeneralityList,
  });

  const showTooltip = useMemo(() => {
    if (valuesPerson?.fiscalCode || (valuesPerson?.name && valuesPerson?.surname)) {
      if (errors?.searchApplicant) {
        return true;
      }
      return false;
    }

    if (!valuesPerson?.fiscalCode || (!valuesPerson?.name && !valuesPerson?.surname)) {
      return true;
    }

    return false;
  }, [valuesPerson, errors?.searchApplicant]);

  const tooltipMessage = useMemo(() => {
    if (valuesPerson?.fiscalCode || (valuesPerson?.name && valuesPerson?.surname)) {
      if (errors?.searchApplicant) {
        return errors?.searchApplicant;
      }
      return;
    }
    if (!valuesPerson?.fiscalCode || (!valuesPerson?.name && !valuesPerson?.surname)) {
      return 'Inserire codice fiscale o cognome e nome';
    }
  }, [valuesPerson, errors?.searchApplicant]);

  const handleSearch = useEventCallback(() => {
    setVisibilityTable(false);

    if (!applicant?.fiscalCode && !(applicant?.name && applicant?.surname)) {
      return;
    }

    setValues({
      accountholder: allValues.accountholder,
      applicant: {
        fiscalCode: allValues.applicant?.fiscalCode,
        surname: allValues.applicant?.surname,
        name: allValues.applicant?.name,
        gender: allValues.applicant?.gender,
        withoutName: allValues.applicant?.withoutName,
        withoutSurname: allValues.applicant?.withoutSurname,
        birthDate: allValues.applicant?.birthDate,
        birthChoiceDate: allValues.applicant?.birthChoiceDate,
      },
      generality: {
        birthDate: allValues.generality?.birthDate,
        birthChoiceDate: allValues.generality?.birthChoiceDate,
        isForeign: allValues.generality?.isForeign,
        foreignStateBirth: allValues.generality?.foreignStateBirth,
        provinceOfBirth: allValues.generality?.provinceOfBirth,
        foreignMunicipality: allValues.generality?.foreignMunicipality,
        municipalityOfBirth: allValues.generality?.municipalityOfBirth,
        locality: allValues.generality?.locality,
        numberDocument: allValues.generality?.numberDocument,
        dateReleaseDocument: allValues.generality?.dateReleaseDocument,
        isForeignDocument: allValues.generality?.isForeignDocument,
        consulateCode: allValues.generality?.consulateCode,
        municipalityDocument: allValues.generality?.municipalityDocument,
        residenceString: isAuthentic || isDeclaration ? allValues.generality?.residenceString : '',
        witness1: isAuthentic ? allValues.generality?.witness1 : '',
        witness1IdentityCard: isAuthentic ? allValues.generality?.witness1IdentityCard : '',
        witness2: isAuthentic ? allValues.generality?.witness2 : '',
        witness2IdentityCard: isAuthentic ? allValues.generality?.witness2IdentityCard : '',
      },
    });

    const data = transformSearchPersonDataset({
      page: 0,
      rowsPerPage: 0,
      orders: {},
      subjectData: {
        fiscalCode: applicant?.fiscalCode,
        name: applicant?.name,
        surname: applicant?.surname,
        withoutName: applicant?.withoutName,
        withoutSurname: applicant?.withoutSurname,
        gender: applicant?.gender,
        birthday: values?.birthDate,
        birthdayChoice: values?.birthChoiceDate,
        isForeign: values?.isForeign,
        foreignMunicipality: values?.foreignMunicipality,
        foreignStateBirth: values?.foreignStateBirth,
        municipalityOfBirth: values?.municipalityOfBirth,
        provinceOfBirth: values?.municipalityOfBirth?.province || undefined,
        // se feature auth anpr === 2
        isNationalResearch: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? true : false,
        municipalityOfResidence:
          userProfile?.datiLogin?.profile?.feature?.anpr === '1'
            ? {
                id: userProfile?.datiLogin?.codiceIstat || '',
                description: userProfile?.datiLogin?.nomeComune || '',
                province: userProfile?.datiLogin?.siglaProvincia || '',
              }
            : undefined,
      },
      identityCard: {
        number: values?.numberDocument,
        // TODO data rilascio
      },
    });

    (
      getAnpr({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificateFormVM>;

      if (castedResponse?.errors?.length > 0) {
        window.scrollTo(0, 0);
        setVisibility({});
        setValues({
          ...allValues,
          visibilityErrorApplicant: true,
          generality: { ...(allValues.generality || {}), searchApplicant: true },
        });
        return;
      }

      if (castedResponse?.persons?.length === 0) {
        setValues({
          ...allValues,
          certifiableApplicant: true,
          generality: { ...(allValues.generality || {}), searchApplicant: true },
          accountholder: hasAccountholder
            ? {
                sameApplicant: false,
                isLocked: true,
              }
            : { sameApplicant: true },
        });
        return;
      }

      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        if (element.anomaly?.length && !isDeclaration) {
          setOpenErrorModal(true);
        }
        setValues({
          ...allValues,
          isAire: element.isAire,
          isBritish: element.isBritish,
          applicant: element.applicant,
          anomaly: element.anomaly,
          certifiableApplicant: element.certifiableApplicant,
          generality: { ...element.generality, searchApplicant: true, isFromAnpr: true },
        });
        window.scrollTo(0, 0);
        return;
      }
      resetPagination();
      setVisibilityTable(true);
    });
  });

  const handleClickSelect = useEventCallback((person: CertificateFormVM) => {
    (
      getPerson({
        data: {
          criteriRicerca: {
            codiceFiscale: person.applicant?.fiscalCode || undefined,
            idSchedaSoggettoANPR: person?.generality?.idSubjectAnpr,
          },
          datiRichiesta: {
            comuneResidenza:
              userProfile?.datiLogin?.profile?.feature?.anpr === '1'
                ? {
                    codiceIstat: userProfile?.datiLogin?.codiceIstat,
                    nomeComune: userProfile?.datiLogin?.nomeComune,
                    siglaProvinciaIstat: userProfile?.datiLogin?.siglaProvincia,
                  }
                : undefined,
            ricercaNazionale: userProfile?.datiLogin?.profile?.feature?.anpr === '2' ? '1' : '0',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CertificateFormVM>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CertificateFormVM>;
      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];
        if (element.anomaly?.length && !isDeclaration) {
          setOpenErrorModal(true);
        }
        setValues({
          ...allValues,
          isBritish: element.isBritish,
          isAire: element.isAire,
          applicant: element.applicant,
          anomaly: element.anomaly,
          certifiableApplicant: element.certifiableApplicant,
          generality: { ...element.generality, searchApplicant: true, isFromAnpr: true },
        });
        window.scrollTo(0, 0);
        setVisibilityTable(false);
      }
      resetPagination();
    });
  });

  const castedAnprData = anpr.data as AnprVM<CertificateFormVM>;

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((person) => ({
        fiscalCode: person.applicant?.fiscalCode || '',
        surname: person.applicant?.surname || '',
        name: person.applicant?.name || '',
        gender: person.applicant?.gender || '',
        birthday: person.generality?.birthDate?.toFormat('dd/MM/yyyy') || '',
        birthplace: person.generality?.birthplace,
        residence: person.generality?.residenceString,
        state: person.generality?.state,
        anpr: person.generality?.idSubjectAnpr,
        action: (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => handleClickSelect(person)}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons]);

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, anpr.data]);

  const handleSurname = useEventCallback((value: string) => {
    onChangePerson('surname', value);
  });

  // Handle name
  const handleName = useEventCallback((value: string) => {
    onChangePerson('name', value);
  });

  // Handle gender
  const handleGender = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangePerson('gender', event.target.value);
  });

  return (
    <Fragment>
      {visibilityTable || errorMessage.visibility ? (
        <div className="pmi-pt-2">
          <SearchMessages
            type={MessageType.Error}
            title={errorMessage.title || ''}
            messages={errorMessage.messages || []}
            visibility={errorMessage.visibility || false}
            onChangeVisibility={onVisibility}
          />
          <SearchMessages
            type={MessageType.Warning}
            title={`Ci sono ${castedAnprData?.warnings?.length || ''} warning`}
            messages={castedAnprData?.warnings || []}
            visibility={
              visibility.warning !== undefined
                ? visibility.warning
                : (castedAnprData?.warnings || [])?.length > 0
            }
            onChangeVisibility={onVisibility}
          />
        </div>
      ) : undefined}

      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Codice fiscale *"
            aria-label="Codice fiscale"
            id="fiscal-code"
            fullWidth
            onChange={(event) => {
              onChangePerson('fiscalCode', event?.target.value);
            }}
            value={valuesPerson?.fiscalCode?.toUpperCase() || ''}
            inputProps={{ maxLength: '16' }}
            disabled={disabled}
            error={touchedPerson?.fiscalCode && Boolean(errorsPerson?.fiscalCode)}
            helperText={errorsPerson?.fiscalCode}
          />
        </Grid>
        <Grid
          item
          container
          spacing={0}
          columnSpacing={{ xs: 0, md: 1 }}
          xs={12}
          md={10}
          alignItems="center"
        >
          <Grid item xs={12} md={9}>
            <InputKeyboard
              label="Cognome *"
              id="surname"
              aria-label="Cognome"
              onChange={handleSurname}
              disabled={disabled}
              value={valuesPerson?.surname || ''}
              touched={touchedPerson?.surname}
              error={errorsPerson?.surname}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="withoutSurname"
                  checked={valuesPerson?.withoutSurname || false}
                  onChange={() => onChangePerson('withoutSurname', !valuesPerson?.withoutSurname)}
                  inputProps={{ 'aria-label': 'Persona senza cognome' }}
                  disabled={disabled}
                />
              }
              label="Persona senza cognome"
            />
          </Grid>
        </Grid>
        <Grid
          item
          container
          spacing={0}
          columnSpacing={{ xs: 0, md: 1 }}
          xs={12}
          md={10}
          alignItems="center"
        >
          <Grid item xs={12} md={9}>
            <InputKeyboard
              label="Nome *"
              aria-label="Nome"
              id="name"
              disabled={disabled}
              onChange={handleName}
              value={valuesPerson?.name || ''}
              touched={touchedPerson?.name}
              error={errorsPerson?.name}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="withoutName"
                  disabled={disabled}
                  checked={valuesPerson?.withoutName || false}
                  onChange={() => onChangePerson('withoutName', !valuesPerson?.withoutName)}
                  inputProps={{ 'aria-label': 'Persona senza nome' }}
                />
              }
              label="Persona senza nome"
            />
          </Grid>
        </Grid>
        <Grid item xs={12} md={5}>
          <ListGender
            required
            value={valuesPerson?.gender || ''}
            onChange={handleGender}
            error={touchedPerson?.gender && Boolean(errorsPerson?.gender)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={0} columnSpacing={{ xs: 0, md: 1 }} rowSpacing={1}>
        <Grid item xs={12} container spacing={0} alignItems="center">
          <ChooseDate
            required
            selected={values?.birthDate || null}
            onChange={handleChooseDateChange}
            choice={values?.birthChoiceDate}
            disabled={disabled}
            label="Data nascita"
            error={touched?.birthDate && Boolean(errors?.birthDate)}
          />
          <ItalianForeignState
            isForeign={values?.isForeign}
            foreignStateBirth={values?.foreignStateBirth}
            foreignMunicipality={values?.foreignMunicipality}
            provinceOfBirth={values?.provinceOfBirth}
            municipalityOfBirth={values?.municipalityOfBirth}
            locality={values?.locality}
            values={values}
            onChange={onChange}
            onChangeValues={onChangeValues || noop}
            errors={errors}
            touched={touched}
            isAuthentic={isAuthentic}
            isDeclaration={isDeclaration}
          />
          <Document
            onChange={onChange}
            values={values}
            errors={errors}
            touched={touched}
            onChangeValues={onChangeValues || noop}
            isAuthentic={isAuthentic}
          />
          {isAuthentic || isDeclaration ? (
            <Grid item container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <PmiTitleDivider>Residenza</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <PmiTextField
                  aria-label="Residenza"
                  label="Residenza *"
                  id="residenceString"
                  name="residenceString"
                  fullWidth
                  type="text"
                  disabled={disabled}
                  variant="outlined"
                  value={values?.residenceString || ''}
                  onChange={(event) => onChange('residenceString', event.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <PmiTextField
                  aria-label="Indirizzo"
                  label="Indirizzo *"
                  id="addressString"
                  name="addressString"
                  fullWidth
                  type="text"
                  disabled={disabled}
                  variant="outlined"
                  value={values?.addressString || ''}
                  onChange={(event) => onChange('addressString', event.target.value)}
                />
              </Grid>
            </Grid>
          ) : undefined}
          <Grid container spacing={2} item xs={12} display="flex" justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <PmiTooltipError show={showTooltip} message={tooltipMessage}>
                <span>
                  <PmiButton
                    color="cdmBrandThird"
                    variant="outlined"
                    onClick={handleSearch}
                    fullWidth
                    id="handle-search-anpr"
                  >
                    Cerca
                  </PmiButton>
                </span>
              </PmiTooltipError>
            </Grid>
          </Grid>
        </Grid>
        {dataPersons.length > 1 && visibilityTable ? (
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista Persone"
              page={paginationState.page}
              rowsPerPage={paginationState.rowsPerPage}
              body={dataPersons}
              count={dataPersons.length}
              head={headCells}
              onSort={noop}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onVisibilityPress={noop}
              orders={{}}
              isNotLayoutFixed
              noSort
              hideButtons
            />
          </Grid>
        ) : undefined}
      </Grid>
      <ErrorModal
        open={openErrorModal}
        onClose={() => {
          setOpenErrorModal(false);
        }}
        dataPersons={allValues.anomaly}
      />
    </Fragment>
  );
};

AccordionGeneralityFormComponent.displayName = 'Accordion generality applicant form component';

const AccordionGeneralityForm = memo(AccordionGeneralityFormComponent);

export default AccordionGeneralityForm;
